import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AdminLoginForm = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check for account lockout (5 failed attempts)
    if (loginAttempts >= 5) {
      setErrors({
        general: 'Account temporarily locked due to multiple failed attempts. Please contact system support.'
      });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);
      
      if (error) {
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        
        let errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        
        if (newAttempts >= 3) {
          errorMessage = `Invalid credentials. ${5 - newAttempts} attempts remaining before account lockout.`;
        }
        
        setErrors({
          general: errorMessage
        });
        setIsLoading(false);
        return;
      }

      // Verify user has sysadmin role
      const userRole = data?.user?.user_metadata?.role;
      
      if (userRole !== 'sysadmin') {
        await signIn(formData?.email, formData?.password); // Sign out
        setErrors({
          general: 'Access denied. This portal is restricted to system administrators only.'
        });
        setIsLoading(false);
        return;
      }

      // Successful login - redirect to system administrator dashboard
      navigate('/system-administrator-dashboard');
    } catch (error) {
      setErrors({
        general: 'An unexpected error occurred. Please try again or contact system support.'
      });
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-6">
      {errors?.general && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-error/10 border border-error/20">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-error font-medium">{errors?.general}</p>
            {loginAttempts >= 3 && loginAttempts < 5 && (
              <p className="text-xs text-error/80 mt-1">
                Security Notice: Multiple failed attempts detected
              </p>
            )}
          </div>
        </div>
      )}
      {/* Security Notice */}
      <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <Icon name="Info" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
        <p className="text-xs text-primary">
          All login attempts are monitored and logged for security purposes.
        </p>
      </div>
      <Input
        label="Administrator Email"
        type="email"
        name="email"
        placeholder="admin@trainarygym.com"
        value={formData?.email}
        onChange={handleInputChange}
        error={errors?.email}
        required
        disabled={isLoading || loginAttempts >= 5}
        autoComplete="email"
      />
      <div className="relative">
        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          name="password"
          placeholder="Enter your secure password"
          value={formData?.password}
          onChange={handleInputChange}
          error={errors?.password}
          required
          disabled={isLoading || loginAttempts >= 5}
          autoComplete="current-password"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[38px] p-2 rounded-lg hover:bg-muted transition-colors duration-250 focus-ring"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          disabled={isLoading || loginAttempts >= 5}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} color="var(--color-muted-foreground)" />
        </button>
      </div>
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => {}}
          className="text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-250 focus-ring rounded px-2 py-1"
          disabled={isLoading || loginAttempts >= 5}
        >
          Forgot Password?
        </button>
      </div>
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        iconName="ShieldCheck"
        iconPosition="right"
        disabled={loginAttempts >= 5}
      >
        {loginAttempts >= 5 ? 'Account Locked' : 'Secure Sign In'}
      </Button>
      {/* Login Attempts Indicator */}
      {loginAttempts > 0 && loginAttempts < 5 && (
        <div className="flex items-center justify-center gap-1">
          {[...Array(5)]?.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full ${
                index < loginAttempts ? 'bg-error' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            ></div>
          ))}
        </div>
      )}
    </form>
  );
};

export default AdminLoginForm;